import { displayDiagnosis, metricLabel } from '../agent-common/diagnosisLabels';
import { entityShowing } from './entityShowing';
import React from 'react';
import { writerWordsStripped } from '../../stores/slices/writerPersisted';
import AnswerContextLine from './AnswerContextLine';
import AnswerDeliveryStatus, { type DeliveryNote } from './AnswerDeliveryStatus';
import PriorWorkCard from './PriorWorkCard';
import HiddenNote from '../Ladder/HiddenNote';
import NextCard from '../Ladder/NextCard';
import { formatKpiValue } from '../agent-common/formatKpiValue';
import ClaimValue from '../Ladder/ClaimValue';
import { answerTable, cellClaim, columnNote, knowledgeBesideNumbers, tableClaims, tileClaim, type Claim, type LadderAnswer, type TableClaims } from '../Ladder/claims';
import { hasSteps, StepTimeline } from './simple/WorkingBlock';
import TraceLink, { traceLinksOn } from '../agent-common/TraceLink';
import WhyCard, { whyParts, WhyEvidence } from '../Ladder/WhyCard';
import DiagnoseTrace from './DiagnoseTrace';
import { InSlot, ResultCard, ResultPanel, type PanelTabSpec, type ReplySlots } from './simple/ResultPanel';
import ExploreAction from './simple/ExploreAction';
import LoopWords from './LoopWords';
import { KnowledgeSections } from './SpecialistAnswerCard';
import SessionTrace from './simple/SessionTrace';
import type { PanelTab } from './simple/resultPanelStore';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import Button from '@mui/material/Button';
import { format as formatSql } from 'sql-formatter';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { specWindow } from '../../stores/slices/navigationSlice';
import ChartBundle from './ChartBundle';
import FeedbackBar from './FeedbackBar';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import type { WorkerResultSnapshot, LandingAgentTurn, UsedDraftDefinition } from '../../stores/slices/landingAgentSlice';
import { followUpOf } from '../../stores/slices/landingAgentSlice';
import { streamedWords } from '../../stores/slices/answerStream';
import { reviewOfSection, sectionTitle } from '../../stores/slices/reviewsSlice';
import { restsOnAReading } from '../../stores/slices/pinsSlice';
import { EvidenceChip } from '../../design/primitives';
import { stromaAnswerCard, stromaCompactPanel, stromaSectionLabel, stromaWalkCard } from './askPresentation';

type TabKey = 'summary' | 'data' | 'sql' | 'graph';

function subTaskLabel(w: WorkerResultSnapshot): string {
  const rm = w.required_metadata || {};
  const dims: string[] = Array.isArray(rm.dimensions) ? rm.dimensions : [];
  if (dims.length) {
    return `By ${dims.join(' × ')}`;
  }
  const metrics: string[] = Array.isArray(rm.metrics) ? rm.metrics : [];
  if (metrics.length) {
    return metrics.slice(0, 2).join(', ') + (metrics.length > 2 ? '…' : '');
  }
  return w.sub_task_id;
}

export function DataTable({ worker, claims, labels, show }: { worker: WorkerResultSnapshot; claims?: TableClaims | null;
  /** the loop's tables (#2203): a column's header in words, and how a cell reads; the Data tab keeps the raw values */
  labels?: Record<string, string>; show?: (column: string, value: unknown) => string }) {
  const columns = worker.columns || (worker.rows && worker.rows[0] ? Object.keys(worker.rows[0]) : []);
  const rows = worker.rows || [];
  if (!columns.length || !rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No rows returned.
      </Typography>
    );
  }
  return (
    <Box sx={{ maxHeight: 420, overflow: 'auto', border: 1, borderColor: 'var(--line-soft)', borderRadius: '10px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                style={{
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderBottom: '2px solid var(--line)',
                  backgroundColor: 'var(--surface2)',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                {labels?.[col] || col}
                {columnNote(claims, col) && (
                  <Typography component="div" variant="caption" color="text.secondary"
                    data-testid="column-note" sx={{ fontWeight: 400, whiteSpace: 'normal', minWidth: 120 }}>
                    {columnNote(claims, col)}
                  </Typography>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} style={{ backgroundColor: rowIdx % 2 === 0 ? 'var(--surface)' : 'var(--surface2)' }}>
              {columns.map((col) => {
                const cell = (row as any)[col];
                const cellStr = cell === null || cell === undefined ? null : String(cell);
                return (
                  <td
                    key={col}
                    style={{
                      padding: '6px 12px',
                      borderBottom: '1px solid var(--sunk)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cellStr === null ? (
                      <span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>null</span>
                    ) : (
                      // Every metric cell is an object (#183): a claim with its
                      // grounding, clickable. Dimension cells stay plain.
                      <ClaimValue claim={cellClaim(claims, rowIdx, col)}>{show ? show(col, cell) : cellStr}</ClaimValue>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

const number = (v: number, digits = 2) => v.toLocaleString(undefined, { maximumFractionDigits: digits });

/** How a cell of a loop table reads: its column's kind, from the catalog's unit (`loop_door.table_of`). */
function loopCell(kind: string | null | undefined, value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!kind || !Number.isFinite(n)) return String(value);
  // to the cent, every cell alike: the words beside the table state the same value
  if (kind === 'currency') return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
  if (kind === 'percent') return `${number(n * 100, 1)}%`;
  return number(n);
}

/** The tables the loop's model asked for (#2203): the columns it named, headers in words, each number a claim. */
function LoopTables({ tables, workers, ladder }: { tables?: any[]; workers: WorkerResultSnapshot[]; ladder: LadderAnswer | null }) {
  return (
    <>
      {(tables || []).map((t, i) => {
        const worker = workers.find((w) => w.sub_task_id === t.worker);
        if (!worker) return null;
        return (
          <Box key={i} sx={{ mb: 2 }} data-testid="loop-table">
            {t.title && <Typography variant="subtitle2" component="div" sx={{ mb: 0.75 }}>{t.title}</Typography>}
            <DataTable worker={{ ...worker, columns: t.columns }} claims={tableClaims(ladder, t.worker)} labels={t.labels}
              show={(col, value) => loopCell(t.kinds?.[col], value)} />
          </Box>
        );
      })}
    </>
  );
}

/** #1489: the answer table of a worker, rendered from its frame by the ladder's indices; null when it has none. */
function workerAnswer(worker: WorkerResultSnapshot, claims: TableClaims | null | undefined) {
  const columns = worker.columns || (worker.rows && worker.rows[0] ? Object.keys(worker.rows[0]) : []);
  return answerTable(claims, columns, worker.rows || []);
}

/** #1489: exactly what was asked, first. A single value reads as one; anything else is a compact table. Every cell
 * is a frame cell (or its year) and carries that cell's claim. */
export function AnswerTable({ worker, claims }: { worker: WorkerResultSnapshot; claims?: TableClaims | null }) {
  const answer = workerAnswer(worker, claims);
  if (!answer) return null;
  const show = (v: unknown) => (v === null || v === undefined ? 'null' : String(v));
  if (answer.columns.length === 1 && answer.rows.length === 1) {
    const [cell] = answer.rows[0];
    return (
      <Box sx={{ mb: 2 }} data-testid="answer-table">
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{answer.columns[0]}</Typography>
        <Typography className="stroma-number" sx={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 26, lineHeight: 1.2 }}>
          <ClaimValue claim={cell.claim}>{show(cell.value)}</ClaimValue>
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ mb: 2, maxHeight: 320, overflow: 'auto' }} data-testid="answer-table">
      <table style={{ borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {answer.columns.map((col) => (
              <th key={col} style={{ padding: '6px 14px 6px 0', textAlign: 'left', borderBottom: '1px solid var(--line)',
                fontWeight: 600, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {answer.rows.map((cells, r) => (
            <tr key={r}>
              {cells.map((cell, k) => (
                <td key={k} style={{ padding: '6px 14px 6px 0', borderBottom: '1px solid var(--sunk)', whiteSpace: 'nowrap' }}>
                  <ClaimValue claim={cell.claim}>{show(cell.value)}</ClaimValue>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

/** #1489: whether a turn's supporting numbers are open, kept per turn in the store (a tab switch or a remount keeps it). */
function useSupportingOpen(turnId: string): [boolean, (open: boolean) => void] {
  const open = useVoyagerStore((s) => !!s.answerSupportingOpen?.[turnId]);
  const set = useVoyagerStore((s) => s.setAnswerSupportingOpen);
  return [open, (next: boolean) => set(turnId, next)];
}

function SupportingToggle({ open, onToggle, detail, testId }: { open: boolean; onToggle: () => void; detail: string; testId: string }) {
  return (
    <Button size="small" variant="text" onClick={onToggle} aria-expanded={open}
      sx={{ textTransform: 'none', px: 0, mb: 1 }} data-testid={testId}>
      {open ? 'Hide' : 'Show'} supporting numbers · {detail}
    </Button>
  );
}

/** #1489: today's table behind a disclosure when the worker has an answer; the table itself when it has none. */
export function SupportingNumbers({ worker, claims, turnId }: { worker: WorkerResultSnapshot; claims?: TableClaims | null; turnId: string }) {
  const [open, setOpen] = useSupportingOpen(turnId);
  if (!workerAnswer(worker, claims)) return <DataTable worker={worker} claims={claims} />;
  const columns = worker.columns || (worker.rows && worker.rows[0] ? Object.keys(worker.rows[0]) : []);
  const rows = worker.row_count ?? (worker.rows || []).length;
  return (
    <>
      <AnswerTable worker={worker} claims={claims} />
      <SupportingToggle open={open} onToggle={() => setOpen(!open)} testId="supporting-numbers"
        detail={`${rows} ${rows === 1 ? 'row' : 'rows'} × ${columns.length} ${columns.length === 1 ? 'column' : 'columns'}`} />
      {open && <DataTable worker={worker} claims={claims} />}
    </>
  );
}

/** #1489: the charts are supporting numbers too: behind the same disclosure when the answer is shown. */
function SupportingCharts({ bundles, ladder, turnId }: { bundles: any[]; ladder: LadderAnswer | null | undefined; turnId: string }) {
  const [open, setOpen] = useSupportingOpen(turnId);
  return (
    <>
      <SupportingToggle open={open} onToggle={() => setOpen(!open)} testId="supporting-charts"
        detail={`${bundles.length} ${bundles.length === 1 ? 'chart' : 'charts'}`} />
      {open && (
        <Stack spacing={2}>
          {bundles.map((b: any, i: number) => <ChartBundle key={i} bundle={b} ladder={ladder} />)}
        </Stack>
      )}
    </>
  );
}

function SubTaskPicker({
  workers,
  selectedId,
  onSelect,
}: {
  workers: WorkerResultSnapshot[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  if (workers.length <= 1) return null;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
      {workers.map((w) => (
        <Chip
          key={w.sub_task_id}
          label={subTaskLabel(w)}
          title={w.description}
          size="small"
          color={w.sub_task_id === selectedId ? 'primary' : 'default'}
          variant={w.sub_task_id === selectedId ? 'filled' : 'outlined'}
          onClick={() => onSelect(w.sub_task_id)}
        />
      ))}
    </Stack>
  );
}

/** What a spec may carry and still open as it ran. The window, order and per-metric options are read below; `limit` is
 *  the Query Builder's own setting, so a ranking opens with every row, in the same order. */
const OPENS_AS_IS = new Set(['perspective', 'metrics', 'dimensions', 'filters', 'dateField', 'dateRangeStart', 'dateRangeEnd',
  'dateRangePreset', 'date_range', 'orderBy', 'limit', 'metricConfigs', 'sqlDialect', 'useDefinitions']);
/** Each thing a query can do that the Query Builder would drop, changing the numbers, in the words the button says. */
const CANNOT_OPEN: Record<string, string> = {
  entity: 'This answer lists records, and the Query Builder works on metrics.',
  bins: 'This query groups values into bins, which the Query Builder can’t do yet.',
  threshold: 'This query keeps only the groups past a threshold, which the Query Builder can’t do yet.',
  derivedFields: 'This query adds calculated columns, which the Query Builder can’t show yet.',
};
const OPENS_METRIC_OPTIONS = new Set(['metricName', 'instanceId', 'alias', 'aggregationOverride', 'timePeriod', 'dateDimension']);

/** Explore's target: the query, or why it can't be opened. */
export type ExploreTarget = { spec: any; reason?: undefined } | { spec: null; reason: string };

/**
 * The query Explore opens for one sub-task: its spec on the days it ran, as explicit dates, so the Query Builder (and
 * any regeneration there) cannot move them. Explicit dates are the spec's own (`specWindow`); a relative preset is the
 * days it resolved to when the answer ran, as the ladder stamped them on the sub-task's claims — re-resolving it today
 * would run other days. It says why not, rather than open something else, when the query cannot be opened as it ran:
 * no perspective, no window (all time, which the Query Builder cannot express), opted-in draft definitions
 * (`useDefinitions`, #151, which it cannot carry), a preset with no single stamped window (a preset beside both dates
 * is stamped with the dates, not it), or anything else it would drop (`CANNOT_OPEN`).
 */
export function exploreTarget(worker: WorkerResultSnapshot | null | undefined, ladder: LadderAnswer | null | undefined): ExploreTarget {
  const no = (reason: string) => ({ spec: null, reason });
  const spec = worker?.spec;
  if (!spec) return no('This answer’s query can’t be opened as one query.');
  if (!spec.perspective) return no('This query isn’t tied to a perspective, so it can’t be opened.');
  for (const key of Object.keys(spec)) {
    const value = spec[key];
    if (OPENS_AS_IS.has(key) || value == null || (Array.isArray(value) && !value.length)) continue;
    return no(CANNOT_OPEN[key] || 'This query uses something the Query Builder can’t show yet.');
  }
  if (!spec.metrics?.length) return no(CANNOT_OPEN.entity);
  if (Array.isArray(spec.useDefinitions) && spec.useDefinitions.length) return no('This answer uses a draft definition, which the Query Builder can’t carry.');
  if ((spec.metricConfigs || []).some((c: any) => Object.keys(c || {}).some((k) => !OPENS_METRIC_OPTIONS.has(k) && c[k] != null))) {
    return no('This query shows a metric as a share, which the Query Builder can’t do yet.');
  }
  const { dateRangePreset: preset, date_range: _range, ...rest } = spec;
  const w = specWindow(spec);
  if (!w && (!preset || preset === 'CUSTOM')) return no('This query ran over all time, and the Query Builder needs a date range.');
  if (w?.dateRangePreset === 'CUSTOM') return { spec: { ...rest, dateRangeStart: w.dateRangeStart, dateRangeEnd: w.dateRangeEnd } };
  const unknownDays = no('The exact days this query ran weren’t recorded, so the Query Builder would show other numbers.');
  if (spec.dateRangeStart && spec.dateRangeEnd) return unknownDays;
  const windows = (tableClaims(ladder, worker!.sub_task_id)?.claims || []).map((c) => c.grounding?.window);
  const ran = windows[0];
  if (!ran?.start || !ran?.end || windows.some((x) => x?.start !== ran.start || x?.end !== ran.end)) return unknownDays;
  return { spec: { ...rest, dateRangeStart: ran.start, dateRangeEnd: ran.end } };
}

export function exploreSpec(worker: WorkerResultSnapshot | null | undefined, ladder: LadderAnswer | null | undefined): any | null {
  return exploreTarget(worker, ladder).spec;
}

function ExploreButton({ spec, turnId }: { spec: any; turnId?: string }) {
  const navigate = useVoyagerStore((s) => s.navigateToWorkspaceWithSpec);
  if (!spec) return null;
  return (
    <Tooltip title="Explore this further in our semantic workspace" arrow>
      <Button
        size="small"
        variant="text"
        startIcon={<OpenInNewIcon fontSize="small" />}
        onClick={() => navigate(spec, { threadId: useVoyagerStore.getState().currentThreadId, turnId: turnId || null })}
        sx={{ textTransform: 'none', py: 0.25, color: 'text.secondary' }}
      >
        Explore
      </Button>
    </Tooltip>
  );
}

function CopyIconButton({ getText, tooltip = 'Copy' }: { getText: () => string; tooltip?: string }) {
  const [copied, setCopied] = React.useState(false);
  const handle = async () => {
    try {
      await navigator.clipboard.writeText(getText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // best-effort; ignore
    }
  };
  return (
    <Tooltip title={copied ? 'Copied' : tooltip} arrow>
      <IconButton size="small" onClick={handle} sx={{ color: 'text.secondary' }}>
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function rowsToTsv(columns: string[], rows: any[]): string {
  const esc = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    // TSV: replace tabs/newlines inside cells with spaces so paste into sheets stays aligned.
    return s.replace(/[\t\r\n]+/g, ' ');
  };
  const header = columns.join('\t');
  const body = rows.map((r) => columns.map((c) => esc((r as any)[c])).join('\t')).join('\n');
  return `${header}\n${body}`;
}

/**
 * The provisional caveat, as a line that survives leaving the app.
 *
 * A copied table and a downloaded CSV are exactly the artifacts that outlive
 * the card the banner lives on — they get pasted into a deck or opened in a
 * spreadsheet, where nothing says the numbers came from wording nobody has
 * reviewed. A comment row costs one line and travels with the file.
 */
function draftNotice(drafts: UsedDraftDefinition[]): string {
  if (!drafts.length) return '';
  const names = drafts.map((d) => d.display_name || d.name).filter(Boolean).join(', ');
  return `# PROVISIONAL: built on unreviewed definition(s) of ${names}`
    + '. Not reviewed — do not circulate as the official figure.';
}

/** The same notice as a well-formed CSV row.
 *
 *  A bare comment line splits on the commas in "Whales, New Users" and hands
 *  an importer a row with a different field count than the header. Quoting
 *  fixes the commas but still leaves one field against the header's N, which
 *  strict parsers also reject. So: quote it, then pad to the column count.
 *  Still visibly a comment to a human, still a legal row to a machine. */
function draftNoticeCsvRow(drafts: UsedDraftDefinition[], columnCount: number): string {
  const notice = draftNotice(drafts);
  if (!notice) return '';
  const first = `"${notice.replace(/"/g, '""')}"`;
  return first + ','.repeat(Math.max(0, columnCount - 1));
}

function rowsToCsv(columns: string[], rows: any[], notice = ''): string {
  const esc = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map(esc).join(',');
  const body = rows.map((r) => columns.map((c) => esc((r as any)[c])).join(',')).join('\n');
  // AFTER the header, never before it. A header-aware parser takes record one
  // as the column names, so a leading notice renames every column to blanks
  // and silently mis-maps the data — the caveat would survive by destroying
  // the file it is attached to. As the first data row it is rectangular,
  // legal, and the first thing anyone sees when the file opens.
  return notice ? `${header}\n${notice}\n${body}` : `${header}\n${body}`;
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function prettifySql(sql?: string): string {
  if (!sql) return '-- no SQL --';
  try {
    return formatSql(sql, { language: 'postgresql', keywordCase: 'upper', tabWidth: 2 });
  } catch {
    return sql;
  }
}

/** What this query is, as its run described it: where a reply rests on an earlier result, that description says so. */
function QueryLine({ worker }: { worker: WorkerResultSnapshot }) {
  if (!worker.description) return null;
  return <Typography variant="body2" data-testid="panel-query-line" sx={{ mb: 0.5, color: 'var(--ink-2)', overflowWrap: 'anywhere' }}>{worker.description}</Typography>;
}

/** The Data tab's body (the card's tab with the flag off, the panel's with it on): the query picker, copy, CSV, rows. */
function DataTabBody({ workers, selectedWorker, onSelect, usedDrafts, ladder, turnId }: {
  workers: WorkerResultSnapshot[]; selectedWorker: WorkerResultSnapshot; onSelect: (id: string) => void;
  usedDrafts: UsedDraftDefinition[]; ladder: LadderAnswer | null; turnId: string;
}) {
  return (
    <>
      <SubTaskPicker workers={workers} selectedId={selectedWorker.sub_task_id} onSelect={onSelect} />
      <QueryLine worker={selectedWorker} />
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {selectedWorker.row_count != null ? `${selectedWorker.row_count} rows` : ''}
          {selectedWorker.execution_time_ms != null ? ` · ${selectedWorker.execution_time_ms}ms` : ''}
          {selectedWorker.rows && selectedWorker.row_count != null && selectedWorker.rows.length < selectedWorker.row_count
            ? ` · showing first ${selectedWorker.rows.length}`
            : ''}
        </Typography>
        <CopyIconButton
          tooltip="Copy as TSV"
          getText={() => {
            const cols = selectedWorker.columns
              || (selectedWorker.rows && selectedWorker.rows[0] ? Object.keys(selectedWorker.rows[0]) : []);
            const tsv = rowsToTsv(cols, selectedWorker.rows || []);
            const notice = draftNotice(usedDrafts);
            return notice ? `${notice}\n${tsv}` : tsv;
          }}
        />
        <Tooltip title="Download CSV" arrow>
          <IconButton
            size="small"
            sx={{ color: 'text.secondary' }}
            onClick={() => {
              const cols = selectedWorker.columns
                || (selectedWorker.rows && selectedWorker.rows[0] ? Object.keys(selectedWorker.rows[0]) : []);
              const notice = draftNoticeCsvRow(usedDrafts, cols.length);
              const csv = rowsToCsv(cols, selectedWorker.rows || [], notice);
              // The filename carries it too — the first thing anyone
              // sees of a downloaded file is what it is called.
              downloadCsv(
                `${selectedWorker.sub_task_id}${notice ? '-provisional' : ''}.csv`,
                csv);
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <SupportingNumbers worker={selectedWorker} claims={tableClaims(ladder, selectedWorker.sub_task_id)} turnId={turnId} />
    </>
  );
}

/** The SQL tab's body (the card's tab with the flag off, the panel's with it on). */
function SqlTabBody({ workers, selectedWorker, onSelect }: {
  workers: WorkerResultSnapshot[]; selectedWorker: WorkerResultSnapshot; onSelect: (id: string) => void;
}) {
  return (
    <>
      <SubTaskPicker workers={workers} selectedId={selectedWorker.sub_task_id} onSelect={onSelect} />
      <QueryLine worker={selectedWorker} />
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <CopyIconButton tooltip="Copy SQL" getText={() => prettifySql(selectedWorker.sql)} />
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          border: 1,
          borderColor: 'var(--line-soft)',
          borderRadius: '10px',
          bgcolor: 'var(--surface2)',
          color: 'var(--ink-2)',
          fontFamily: 'var(--mono)',
          fontSize: 13,
          lineHeight: 1.5,
          overflow: 'auto',
          maxHeight: 480,
          whiteSpace: 'pre',
        }}
      >
        {prettifySql(selectedWorker.sql)}
      </Box>
    </>
  );
}

/** The SQL a walk ran (#1507, the panel's SQL tab for a why): each measurement with its statement. */
function WalkSqlBody({ activity }: { activity: any[] }) {
  return (
    <Stack spacing={1.5} data-testid="walk-sql">
      {activity.map((a: any, i: number) => (
        <Box key={i}>
          <Typography variant="caption" sx={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>
            {i + 1}. {a.metric} · {a.windows} window{a.windows === 1 ? '' : 's'}{a.group_by ? ` · by ${a.group_by}` : ''}
          </Typography>
          {a.sql && (
            <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1.5, border: 1, borderColor: 'var(--line-soft)', borderRadius: '10px', bgcolor: 'var(--surface2)',
              color: 'var(--ink-2)', fontFamily: 'var(--mono)', fontSize: 12.5, lineHeight: 1.5, overflow: 'auto', whiteSpace: 'pre' }}>
              {prettifySql(a.sql)}
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );
}

/** #1500 (flag on): the "Related" earlier analyses this answer names, which its panel's Context tab holds. */
function relatedLines(turn: LandingAgentTurn | undefined) {
  const prior = turn?.priorWork;
  return prior?.type === 'prior_work_related' ? (prior.lines || []).filter((l) => l.href && l.text) : [];
}

/** #1500 (flag on): whether this turn's answer card hosts its "Related" prior-work line in the panel (so the turn
 *  leaves it out inline): a walk or a data answer, which always has a result card when it has the line. */
export function panelHoldsRelated(turn: LandingAgentTurn): boolean {
  return relatedLines(turn).length > 0 && !turn.document && (!!turn.result?.diagnose || isDataAnswer(turn.result));
}

/** #1500 (flag on): the panel's Context tab — what the answer checked around itself, moved out of the answer: the
 *  earlier analyses it names, a context check that did not come back, and the answer's context (range, what people
 *  said, owner, agents), and the answer's key-number tiles. */
function ContextTabBody({ turn, context, perspective, delivery, keyNumbers }: {
  turn?: LandingAgentTurn; context?: LadderAnswer['context'] | null; perspective?: string | null; delivery: DeliveryNote | null;
  /** the answer's key-number tiles: inline the answer leads with its sentence, as the mock's does */
  keyNumbers?: React.ReactNode;
}) {
  return (
    <>
      {keyNumbers}
      {turn && relatedLines(turn).length > 0 && <PriorWorkCard turn={turn} />}
      {delivery && (
        <Box role="status" data-testid="panel-delivery-note">
          <Typography variant="caption" color="text.secondary">{delivery.message}</Typography>
          {delivery.retry && <Button size="small" onClick={delivery.retry}>Retry loading details</Button>}
        </Box>
      )}
      {context && <AnswerContextLine context={context} perspective={perspective || ''} />}
    </>
  );
}

/** The Trace tab's body: how the answer was worked out as a short timeline, then the session's every event in the panel
 *  and today's Debug UI link ("Open the full trace") where trace links are on. */
function TraceTabBody({ turn }: { turn: LandingAgentTurn }) {
  const sessionId = turn.sessionId;
  const links = !!sessionId && traceLinksOn();
  const steps = hasSteps(turn);
  const [every, setEvery] = React.useState(false);
  return (
    <>
      {links && (every || !steps) ? <SessionTrace sessionId={sessionId as string} /> : <StepTimeline turn={turn} />}
      {links && (
        <Box data-testid="trace-tab-actions" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TraceLink sessionId={sessionId as string} label="Open the full trace" />
          {steps && (
            <Button size="small" variant="text" onClick={() => setEvery((v) => !v)} data-testid="trace-every-event"
              sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {every ? 'Back to the steps' : 'Show every event'}
            </Button>
          )}
        </Box>
      )}
    </>
  );
}

/** The panel's Trace tab: the steps the answer logged, and its session where trace links are on for this deployment. */
function traceTab(turn: LandingAgentTurn | undefined): PanelTabSpec[] {
  if (!turn || (!hasSteps(turn) && !(turn.sessionId && traceLinksOn()))) return [];
  return [{ key: 'trace', body: () => <TraceTabBody turn={turn} /> }];
}

/** What an answer of one number leads with: the number, the word beside it, and its claim. */
export interface LeadFigure { value: string; unit: string; claim: Claim | null; worker: string | null }

/**
 * The word beside a figure: the first display label the frame carries, never a column alias (the column's own name, or
 * an identifier), lower-cased as prose says it ("Policies" → "policies"; an acronym such as GMV kept). None → ''.
 */
export function figureUnit(column: string | null, ...labels: Array<string | null | undefined>): string {
  const label = labels.find((l): l is string => typeof l === 'string' && !!l.trim() && l.trim() !== column
    && !l.includes('_') && !/^\w+(\.\w+)+$/.test(l.trim()));
  if (!label) return '';
  return label.trim().split(/\s+/).map((w) => (/^[A-Z0-9]{2,}$/.test(w) ? w : w.toLowerCase())).join(' ');
}

/**
 * Whether the answer is one number, and which: a loop table of one cell, the answer table of one cell, a single
 * one-column, one-row result, else a lone key number. Null for anything with rows or columns to compare.
 */
export function leadFigure(result: LandingAgentTurn['result'] | null | undefined): LeadFigure | null {
  if (!result || result.diagnose) return null;
  const ladder = (result.ladder || null) as LadderAnswer | null;
  const synthesis = result.synthesis as any;
  const done = (result.workerResults || []).filter((w) => w.status === 'done');
  const tables: any[] = synthesis?.loop ? (synthesis.tables || []) : [];
  const shownTables = tables.filter((t) => done.some((w) => w.sub_task_id === t.worker));
  const rest = done.filter((w) => !tables.some((t) => t.worker === w.sub_task_id));
  if ((result.bundles || []).length) return null;
  if (shownTables.length + rest.length > 1) return null;
  const columnsOf = (w: WorkerResultSnapshot) => w.columns || (w.rows && w.rows[0] ? Object.keys(w.rows[0]) : []);
  if (shownTables.length === 1) {
    const t = shownTables[0];
    const w = done.find((x) => x.sub_task_id === t.worker)!;
    if ((t.columns || []).length !== 1 || (w.rows || []).length !== 1) return null;
    const col = t.columns[0];
    const v = (w.rows![0] as any)[col];
    if (v === null || v === undefined) return null;
    const claim = cellClaim(tableClaims(ladder, w.sub_task_id), 0, col);
    return { value: loopCell(t.kinds?.[col], v), unit: figureUnit(col, t.labels?.[col], claim?.label), claim, worker: w.sub_task_id };
  }
  if (rest.length === 1) {
    const w = rest[0];
    const claims = tableClaims(ladder, w.sub_task_id);
    const answer = workerAnswer(w, claims);
    if (answer) {
      if (answer.columns.length !== 1 || answer.rows.length !== 1) return null;
      const [cell] = answer.rows[0];
      if (cell.value === null || cell.value === undefined) return null;
      // the word is the projected column's own label; a claim's label only when the cell IS that metric's value (a
      // dimension answer — "which year", "which customer" — carries the ranking metric's claim, never its word)
      const source = claims?.answer?.sources?.[0] ?? null;
      const own = !!source && !!claims?.metric_columns?.includes(source);
      return { value: String(cell.value), unit: figureUnit(source, answer.columns[0], own ? cell.claim?.label : null),
        claim: cell.claim, worker: w.sub_task_id };
    }
    const columns = columnsOf(w);
    if (columns.length !== 1 || (w.rows || []).length !== 1) return null;
    const v = (w.rows![0] as any)[columns[0]];
    const claim = cellClaim(claims, 0, columns[0]);
    if (v === null || v === undefined || !claim) return null;
    return { value: claim.format ? formatKpiValue(v, claim.format) : typeof v === 'number' ? number(v) : String(v),
      unit: figureUnit(columns[0], claim.label), claim, worker: w.sub_task_id };
  }
  const kns = (synthesis?.key_numbers || []) as any[];
  if (kns.length === 1 && kns[0]?.value != null) {
    return { value: formatKpiValue(kns[0].value, kns[0].format), unit: figureUnit(null, tileClaim(ladder, 0)?.label, kns[0].label), claim: tileClaim(ladder, 0), worker: null };
  }
  return null;
}

/** The one number an answer leads with: large, its word beside it (the mock's figure). */
function Figure({ figure }: { figure: LeadFigure }) {
  return (
    <Box data-testid="answer-figure" sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography component="span" className="stroma-number" data-testid="answer-figure-value"
        sx={{ fontSize: 44, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: 'var(--ink)' }}>
        <ClaimValue claim={figure.claim}>{figure.value}</ClaimValue>
      </Typography>
      {figure.unit && <Typography component="span" data-testid="answer-figure-unit" sx={{ fontSize: 15, color: 'var(--ink-2)' }}>{figure.unit}</Typography>}
    </Box>
  );
}

const fmtSignedPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

/**
 * The WHAT rung above a walk (#184, the mock): the number the walk explains
 * — this window's value, the move from the prior window, the window itself
 * — read off the envelope's target, never re-derived here.
 */
export function WhatRung({ target, label, format }: { target: any; label?: string | null;
  /** the number's own format when the walk was opened from one; the target's format hint otherwise */
  format?: string | null }) {
  if (!target || typeof target.current !== 'number') return null;
  const fmt = format || target.format || null;
  const prior = typeof target.prior === 'number' ? target.prior : null;
  const pct = prior != null && prior !== 0 ? ((target.current - prior) / Math.abs(prior)) * 100 : null;
  const dates = target.window_start && target.window_end
    ? `${String(target.window_start).slice(0, 10)} → ${String(target.window_end).slice(0, 10)}` : null;
  return (
    <Paper variant="outlined" sx={{ mt: 1, display: 'grid', gap: 0.5, ...stromaWalkCard }} data-testid="what-rung">
      <Typography variant="caption" sx={stromaSectionLabel}>What</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label || target.label || metricLabel(target.metric)}</Typography>
        <Typography className="stroma-number" sx={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'primary.dark' }}>{formatKpiValue(target.current, fmt)}</Typography>
        {pct != null && (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: pct >= 0 ? 'var(--ok)' : 'var(--bad)' }} data-testid="what-rung-delta">
            {pct >= 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}% <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>vs prior {formatKpiValue(prior, fmt)}</Typography>
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {target.metric}{dates ? ` · ${dates}` : ''}{typeof target.trailing_mean === 'number' ? ` · trailing mean ${formatKpiValue(target.trailing_mean, fmt)}` : ''}
      </Typography>
    </Paper>
  );
}

const INHERITED_LABEL: Record<string, string> = { metrics: 'metric', dimensions: 'grouping', filters: 'filters', window: 'window' };

/** What this answer took from the thread rather than from the question — said on the answer, so an inherited window is never silent. */
export function InheritedChip({ inherited }: { inherited?: Array<{ field: string; source: string }> | null }) {
  const fields = (inherited || []).map((i) => INHERITED_LABEL[i.field] || i.field);
  if (!fields.length) return null;
  return (
    <Chip size="small" variant="outlined" data-testid="inherited-chip" label={`inherited from the thread: ${fields.join(', ')}`}
      sx={{ height: 20, fontSize: '0.68rem', color: 'text.secondary', mb: 0.5 }} />
  );
}

/** The perspective a walk's correction is made in (#186): the turn's, its answer's, else the thread's pick. */
export function whyPerspectiveOf(turn: LandingAgentTurn | undefined, threadPick: string | null): string | null {
  return turn?.perspectiveId || turn?.result?.ladder?.perspective || threadPick || null;
}

/** Whether the answer card says this result as a data answer (#1508's Correct this): not a walk, not another card's
 *  answer (catalog, knowledge/help/decline, prior work), and something to say. */
export function isDataAnswer(result: LandingAgentTurn['result'] | null | undefined): boolean {
  if (!result || result.diagnose) return false;
  const ladder = (result.ladder || null) as LadderAnswer | null;
  if (ladder?.catalog || ladder?.prior_work || (ladder?.specialist && !knowledgeBesideNumbers(result))) return false;
  return !!(result.synthesis || result.formatterSummary || (result.formatterHighlights || []).length || (result.bundles || []).length
    || (result.workerResults || []).some((w) => w.status === 'done'));
}

/** The sub-tasks the Data and SQL tabs list: each period comparison first, then every finished worker none folds in.
 *  The first is the one they open on. */
export function answerWorkers(result: LandingAgentTurn['result'] | null | undefined): WorkerResultSnapshot[] {
  const bundles = result?.bundles || [];
  const completedWorkers = (result?.workerResults || []).filter((w) => w.status === 'done');
  const comparisons: WorkerResultSnapshot[] = bundles.filter((b: any) => b.periods?.length).map((b: any) => ({
    sub_task_id: b.upstream_id, status: 'done', description: 'Period comparison',
    columns: b.table?.columns || [], rows: b.table?.rows || [],
    row_count: b.periods.reduce((total: number, p: any) => total + p.row_count, 0),
    sql: b.periods.map((p: any) => `-- ${p.label}\n${p.sql || ''}`).join('\n\n'),
    used_draft_definitions: completedWorkers.filter((w) => b.periods.some((p: any) => p.sub_task_id === w.sub_task_id))
      .flatMap((w) => w.used_draft_definitions || []),
  }));
  const comparedIds = new Set(bundles.flatMap((b: any) => (b.periods || []).map((p: any) => p.sub_task_id)));
  return [...comparisons, ...completedWorkers.filter((w) => !comparedIds.has(w.sub_task_id))];
}

/** The query an answer rests on: of what it shows (its tables, then its charts, then each number it states, in order,
 *  then what the Data tab lists), the first that can be opened as it ran, else simply the first. */
export function restingWorker(result: LandingAgentTurn['result'] | null | undefined): WorkerResultSnapshot | undefined {
  const workers = answerWorkers(result);
  const ladder = result?.ladder as LadderAnswer | null | undefined;
  const synthesis = result?.synthesis as any;
  const tableOf = (claim: string) => ladder?.what?.tables?.find((t) => t.claims?.some((c) => c.id === claim))?.sub_task_id;
  const shown = [
    ...(synthesis?.tables || []).map((t: any) => t.worker),
    ...(result?.bundles || []).filter((b: any) => !b.periods?.length).map((b: any) => b.upstream_id),
    ...(synthesis?.links || []).map((l: any) => tableOf(l.claim)),
  ].flatMap((id) => workers.filter((w) => w.sub_task_id === id));
  const ranked = [...shown, ...workers];
  return ranked.find((w) => exploreTarget(w, ladder).spec) || ranked[0];
}

/** Whether the answer's row carries Trace: a data answer's and a walk's do; any other answer has the link beside its actions. */
export function rowHoldsTrace(turn: LandingAgentTurn): boolean {
  return !turn.document && !!turn.result && (!!turn.result.diagnose || isDataAnswer(turn.result));
}

/** What Explore opens for an answer, or null when it has no query to open: a walk, an ask, another card's answer or a file's. */
export function exploreOf(turn: LandingAgentTurn): ExploreTarget | null {
  if (turn.document || turn.clarification || turn.definitionRequest || turn.status === 'running' || !isDataAnswer(turn.result)) return null;
  const worker = restingWorker(turn.result);
  return worker ? exploreTarget(worker, turn.result?.ladder as LadderAnswer | null) : null;
}

/**
 * The answer's two pivots (the anomaly bridge, #117 lite): "Why" asks why it changed about THIS answer (`followUpOf`),
 * and What's next walks from its number, else asks in the answer's own perspective (#767: the thread's pick stays).
 * The card's buttons (flag off) and the follow-up chips (flag on, #1587) read this one definition. Null when the turn
 * has no data answer to pivot from.
 */
export function answerPivots(turn: LandingAgentTurn | undefined, result: LandingAgentTurn['result'] | null | undefined) {
  if (!result || !isDataAnswer(result)) return null;
  const ladder = (result.ladder || null) as LadderAnswer | null;
  const synthesis = result.synthesis;
  const headline = synthesis?.headline || result.formatterTitle;
  const narrative = synthesis?.narrative || result.formatterSummary;
  const worked = (result.workerResults || []).some((w) => w.status === 'done') || (result.bundles || []).some((b: any) => b.periods?.length);
  if (!(synthesis || narrative || headline || worked)) return null;
  const normal = ladder?.context?.significance.kind === 'within_normal';
  return {
    why: normal ? null : {
      label: ladder?.context?.significance.kind === 'significant' ? '⚡ Why' : 'Trace why',
      ask: () => useVoyagerStore.getState().sendLandingAgentQuery('Why did that change?', followUpOf(turn)),
    },
    next: {
      disabled: !!turn && turn.status !== 'done',
      ask: () => {
        const candidates = [...(ladder?.what?.tiles || []), ...(ladder?.what?.tables || []).flatMap((t) => t.claims)];
        const claim = candidates.find((c) => c.grounding?.metric_qid && c.grounding.window?.start && c.grounding.window?.end);
        if (claim) useVoyagerStore.getState().askWhyForClaim(claim, claim.label || claim.grounding?.metric || 'this number', null, { intent: 'next' });
        else {
          // asked where this answer was, for this question only (#767: the thread's pick stays)
          const perspective = turn?.perspectiveId || ladder?.perspective || undefined;
          useVoyagerStore.getState().sendLandingAgentQuery(`What should we do next about this answer? Question: ${turn?.userText || ladder?.question || 'the displayed result'}. Answer: ${headline || narrative || ''}`, { threadContext: ladder?.thread_context || null, ...(perspective ? { perspectiveId: perspective } : {}) });
        }
      },
    },
  };
}

export default function AnswerCard({ turn, simple = false, correctRequest, slots }: {
  turn?: LandingAgentTurn;
  /** #1506 (flag on): the turn's action row carries Trace, feedback and the follow-ups, so the card leaves them out */
  simple?: boolean;
  /** Where the turn wants the answer's quiet row and what it opens; rendered in place without. */
  slots?: ReplySlots;
  /** #1506 (flag on): bumped by the action row's Correct this, which opens the walk's correction */
  correctRequest?: number;
} = {}) {
  // why-engine answers (#139) render as the trace, not the mission layout
  const storeResult = useVoyagerStore((s) => s.landingAgentResult);
  const digDeeper = useVoyagerStore((s) => s.digDeeper);
  const deeperRunning = useVoyagerStore(s => !!turn && s.landingAgentTurns.some(t =>
    t.diagnosisParentTurnId === turn.id && (t.status === 'running' || t.diagnosisCancelPending || t.diagnosisDisconnected)));
  const correctWhyTurn = useVoyagerStore((s) => s.correctWhyTurn);
  const contributeWhyTurn = useVoyagerStore((s) => s.contributeWhyTurn);
  const raiseBar = useVoyagerStore((s) => s.raiseBar);
  const currentThreadId = useVoyagerStore((s) => s.currentThreadId);
  const answersLoading = useVoyagerStore((s) => !!s.currentThreadId && s.threadAnswers?.[s.currentThreadId] === 'loading');
  const selectedPerspectiveId = useVoyagerStore((s) => s.selectedPerspectiveId);
  const storedResult = turn ? turn.result : storeResult;
  const shownDiagnosis = storedResult?.diagnose ? displayDiagnosis(storedResult.diagnose) : null;
  const result = shownDiagnosis ? { ...storedResult!, diagnose: shownDiagnosis } : storedResult;
  const sendLandingAgentQuery = useVoyagerStore((s) => s.sendLandingAgentQuery);
  // A walk opened from a review section (#189): its verdict can be written
  // back onto the section as commentary, so the review carries the answer.
  const origin = useVoyagerStore((s) => {
    const t = s.savedThreads.find((x) => x.id === s.currentThreadId);
    return t?.sourceKind === 'review_section' ? reviewOfSection(s.savedThreads, t.sourceId) : null;
  });
  const setReviewSectionCommentary = useVoyagerStore((s) => s.setReviewSectionCommentary);
  const openVerdictPanel = useVoyagerStore((s) => s.openVerdictPanel);
  // A walk opened for WHAT NEXT lands on ITS rung once it is there — this
  // card's, not the first one in a thread that holds several walks.
  const walkRef = React.useRef<HTMLDivElement | null>(null);
  const landedNext = !!(turn?.intent === 'next' && turn?.status === 'done');
  React.useEffect(() => {
    if (!landedNext) return;
    const el = walkRef.current?.querySelector('[data-testid="next-card"]') as HTMLElement | null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [landedNext]);
  // idle → saving → added; a save that fails goes back to idle with the reason, so it can be tried again
  const [commentary, setCommentary] = React.useState<{ state: 'idle' | 'saving' | 'added'; error: string | null }>({ state: 'idle', error: null });

  const synthesis = result?.synthesis;
  // C8 (#1672): the headline and highlights as they are written, where the finished ones render, until each one's
  // final frame replaces them; a stopped answer keeps what it had shown
  const streamed = streamedWords(turn?.streaming);
  const stoppedWords = turn?.stopped ? turn.stoppedWords : null;
  // F1 (#1806): a data answer the Writer wrote shows its rendered highlights, never the formatters' words; an answer
  // with no Writer record (an older answer) shows the formatters' highlights as before
  const highlights = synthesis?.writer
    ? [...(synthesis.highlights || [])]
    : [...(result?.formatterHighlights || []), ...streamed.highlights];
  const headline = synthesis?.headline || result?.formatterTitle;
  const shownHeadline = synthesis?.headline || streamed.headline || result?.formatterTitle;
  const narrative = synthesis?.narrative || result?.formatterSummary;
  const bundles = result?.bundles || [];
  const completedWorkers = (result?.workerResults || []).filter((w) => w.status === 'done');
  const workers = answerWorkers(result);
  const ladder = (result?.ladder || null) as LadderAnswer | null;
  const normal = ladder?.context?.significance.kind === 'within_normal';

  const [tab, setTab] = React.useState<TabKey>('summary');
  // with the flag on (#1500): a settled context-check note, said in the panel's Context tab rather than inline
  const [deliveryNote, setDeliveryNote] = React.useState<DeliveryNote | null>(null);
  const [selectedSubTaskId, setSelectedSubTaskId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (workers.length && !workers.find((w) => w.sub_task_id === selectedSubTaskId)) {
      setSelectedSubTaskId(workers[0].sub_task_id);
    }
  }, [workers, selectedSubTaskId]);

  // with the flag on (#1587): a small muted line, only when the answer used more than one perspective (or one carries
  // provisional definitions, which stay inline)
  const provisionalPerspective = !!result?.selectedPerspectives?.some(p => p.description?.includes('Provisional assumptions'));
  const showSelection = !!result?.selectedPerspectives?.length && (!simple || result.selectedPerspectives.length > 1 || provisionalPerspective);
  const selectionDetails = (showSelection && <Box data-testid="planned-perspectives" sx={simple
          ? { pb: 0.75, color: 'var(--ink-3)', fontSize: 12.5 }
          : { px: 2, py: 1.25, borderBottom: 1, borderColor: 'var(--line-soft)', bgcolor: 'var(--surface2)' }}>
        <Typography variant="caption" sx={simple ? { color: 'var(--ink-3)', mr: 1 } : stromaSectionLabel} color="text.secondary">Planned perspectives</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', ...(simple ? { display: 'inline-flex' } : {}) }}>
          {result!.selectedPerspectives!.map(p => <Tooltip key={p.id} title={p.description || p.name} describeChild>
            <Chip size="small" variant="outlined" label={p.name} onClick={() => useVoyagerStore.getState().pickPerspective(p.id)} sx={{ borderRadius: 99, bgcolor: 'var(--surface)', ...(simple ? { height: 20, fontSize: 12 } : {}) }} />
          </Tooltip>)}
        </Stack>
        {provisionalPerspective && <details>
          <summary>Provisional definitions and limitations</summary>
          {result!.selectedPerspectives!.map(p => <Typography key={p.id} variant="body2" sx={{ whiteSpace: 'pre-line', my: 1 }}>{p.name}: {p.description}</Typography>)}
        </details>}
        {!simple && <Typography variant="caption" color="text.secondary">Select a name to use that perspective for your next question. Separate perspectives are queried independently.</Typography>}
      </Box>);

  // A why-answer is the WHY card (#184): verdict, the four steps, the top
  // drivers, and the full trace behind one toggle. The walk is a traced
  // session now, so the card carries the same Trace link as any answer.
  if (result?.diagnose) {
    // A correction is made IN a perspective (#186); without one there is nothing to bind it to.
    const whyPerspective = whyPerspectiveOf(turn, selectedPerspectiveId);
    const headline = storedResult!.diagnose.verdict?.headline || '';
    const addCommentary = async () => {
      if (!origin || !headline || commentary.state !== 'idle') return;
      const existing = (origin.section.commentary || '').trim();
      if (existing.includes(headline)) { setCommentary({ state: 'added', error: null }); return; }
      setCommentary({ state: 'saving', error: null });
      const ok = await setReviewSectionCommentary(origin.review.id, origin.section.id, existing ? `${existing}\n\n${headline}` : headline);
      // "Added" only once the server has it; a failed save keeps the button live and says why
      setCommentary(ok ? { state: 'added', error: null } : { state: 'idle', error: useVoyagerStore.getState().reviewsError || 'Could not save the commentary' });
    };
    const walk = (
      <Box ref={walkRef} data-testid="walk" data-turn-id={turn?.id || ''} sx={{ display: 'grid', gap: 1.25 }}>
      {selectionDetails}
      <InheritedChip inherited={turn?.inherited} />
      {/* what the loop said about the walk (#2203): a headline that is only the walk's own verdict is not said twice */}
      {synthesis?.loop && ((!!synthesis.headline && synthesis.headline !== headline) || !!synthesis.narrative) && (
        <Box sx={{ maxWidth: '72ch' }} data-testid="loop-walk-words">
          {!!synthesis.headline && synthesis.headline !== headline && (
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.45, fontSize: 16 }}>{synthesis.headline}</Typography>
          )}
          {!!synthesis.narrative && <LoopWords text={synthesis.narrative} />}
        </Box>
      )}
      <WhatRung target={result.diagnose.target} label={origin ? sectionTitle(origin.section) : null} format={origin?.section.claim?.format || null} />
      <Typography variant="caption" sx={{ display: 'block', mt: 1, ...stromaSectionLabel }}>Why</Typography>
      <WhyCard
        envelope={storedResult!.diagnose}
        sessionId={turn?.sessionId || null}
        digging={turn?.status === 'running'}
        onKeepDigging={undefined}
        perspective={whyPerspective}
        onCorrected={turn && whyPerspective ? (saved) => correctWhyTurn(turn.id, saved) : undefined}
        onContributed={turn && whyPerspective ? (saved) => contributeWhyTurn(turn.id, saved) : undefined}
        onRaiseBar={turn ? (bar) => raiseBar(turn.id, bar) : undefined}
        correctRequest={simple ? correctRequest ?? 0 : undefined}
        simple={simple}
      />
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid="walk-actions">
        {origin && headline && turn?.status !== 'running' && !restsOnAReading(ladder) && (
          <Button size="small" variant="contained" onClick={addCommentary} disabled={commentary.state !== 'idle'} data-testid="add-to-review-commentary">
            {commentary.state === 'added' ? `Added to ${origin.review.title} ✓` : commentary.state === 'saving' ? 'Adding…' : 'Add to review commentary'}
          </Button>
        )}
        {turn && result.diagnose.budget?.effort !== 'deep' && !['within_normal', 'immature'].includes(result.diagnose.verdict?.kind) && (
          <Button size="small" variant="outlined" disabled={turn.status === 'running' || deeperRunning} onClick={() => digDeeper(turn.id)} data-testid="keep-digging">
            {turn.status === 'running' || deeperRunning ? 'Digging…' : 'Keep digging'}
          </Button>
        )}
        <Button size="small" variant="outlined" data-testid="why-this-verdict"
          onClick={() => openVerdictPanel({ kind: 'envelope', envelope: storedResult!.diagnose, sessionId: turn?.sessionId || null, title: turn?.userText || null, turnId: turn?.id || null, perspective: whyPerspective })}>
          Why this verdict
        </Button>
        {commentary.error
          ? <Typography variant="caption" color="error" data-testid="add-to-review-commentary-error">{commentary.error}</Typography>
          : origin && commentary.state === 'idle' && <Typography variant="caption" sx={{ color: 'text.secondary' }}>lands on {sectionTitle(origin.section)} in {origin.review.title}</Typography>}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, ...stromaSectionLabel }}>What next</Typography>
      <NextCard formatText={text => displayDiagnosis({ ...storedResult!.diagnose, text }).text} suggestions={ladder?.next as any} note={ladder?.next_note} policy={ladder?.policy as any} threadId={turn ? currentThreadId : null}
                sessionId={turn?.sessionId || null} restsOnReading={restsOnAReading(ladder)}
                onRaise={turn ? (bar) => raiseBar(turn.id, bar) : undefined} raising={turn?.status === 'running'} />
      </Box>
    );
    if (!simple) return walk;
    // #1507 (flag on): the walk's result card, opening the panel on Evidence, Data (the full trace), SQL and Trace
    const owner = turn?.id || 'current';
    const envelope = result.diagnose;
    const { tiered } = whyParts(envelope);
    const label = envelope.target?.label || metricLabel(envelope.target?.metric);
    const whyTitle = envelope.verdict?.kind === 'within_normal' ? `Whether ${label} changed` : `Why ${label} changed`;
    const activity: any[] = (envelope.activity || []).filter((a: any) => a?.sql);
    const queries = envelope.budget?.queries;
    const whySubtitle = [
      tiered.length ? `${tiered.length} driver${tiered.length === 1 ? '' : 's'}` : null,
      (envelope.steps || []).length ? `${envelope.steps.length} steps` : null,
      typeof queries === 'number' ? `${queries} quer${queries === 1 ? 'y' : 'ies'}` : null,
    ].filter(Boolean).join(' · ');
    const whyTabs: PanelTabSpec[] = [
      { key: 'evidence', body: () => <WhyEvidence envelope={storedResult!.diagnose} onRaiseBar={turn ? (bar) => raiseBar(turn.id, bar) : undefined} digging={turn?.status === 'running'} /> },
      { key: 'data', body: () => <DiagnoseTrace envelope={storedResult!.diagnose} /> },
      ...(activity.length ? [{ key: 'sql' as PanelTab, body: () => <WalkSqlBody activity={activity} /> }] : []),
      ...traceTab(turn),
      ...(relatedLines(turn).length ? [{ key: 'context' as PanelTab, body: () => <ContextTabBody turn={turn} delivery={null} /> }] : []),
    ];
    return (
      <>
        {walk}
        <InSlot at={slots?.row ?? null} slotted={!!slots}><ResultCard owner={owner} tabs={whyTabs.map((t) => t.key)} /></InSlot>
        <InSlot at={slots?.panel ?? null} slotted={!!slots}><ResultPanel owner={owner} subtitle={[whyTitle, whySubtitle].filter(Boolean).join(' · ')} tabs={whyTabs} /></InSlot>
      </>
    );
  }

  // a catalog answer (#761) is CatalogAnswerCard's; a knowledge, help or decline answer (#764) is SpecialistAnswerCard's;
  // a list of earlier analyses (#911) is PriorWorkListCard's
  if (!isDataAnswer(result)) {
    return null;
  }

  const selectedWorker = workers.find((w) => w.sub_task_id === selectedSubTaskId) || restingWorker(result) || workers[0];
  // Unreviewed drafts this answer was built on (#151). Derived from the
  // sub-tasks that are actually being SHOWN, so a retried attempt's
  // provenance dies with the snapshot it replaced. Rendered outside the tabs
  // on purpose: the caveat belongs to the number, and the number is visible
  // on every tab.
  const usedDrafts: UsedDraftDefinition[] = [];
  for (const w of workers) {
    for (const d of w.used_draft_definitions || []) {
      if (!usedDrafts.some((x) => x.name === d.name)) usedDrafts.push(d);
    }
  }
  const hasGraph = bundles.some((b: any) => (b?.visualization?.vega_lite) || (b?.visualization?.kpi));
  // #1174 §4.11.4 (G13): each accepted result's code-built label line, shown first — never model text
  const labels = Array.from(new Set(completedWorkers.map((w) => w.label).filter((l): l is string => !!l)));
  // #1174 §4.13: the gate-off post-answer checks' code-built lines, under the label line
  const checkLines = Array.from(new Set(completedWorkers.flatMap((w) => [
    ...(w.caveats || []),
    // #1499: an entity part's count line, from the rows this card carries
    ...(entityShowing(w.entity, (w.rows || []).length) ? [entityShowing(w.entity, (w.rows || []).length) as string] : []),
  ])));

  // the mock's figure: a finished answer of one number leads with it (the panel's Data tab keeps its table)
  const figure = simple && turn?.status !== 'running' && !stoppedWords && !writerWordsStripped(turn) ? leadFigure(result) : null;
  const keyNumberTiles = (synthesis?.key_numbers && synthesis.key_numbers.length > 0 && (() => {
    const kns = (synthesis.key_numbers as any[]).slice(0, 4);
    const cols = Math.min(kns.length, 4);
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr 1fr',
            sm: `repeat(${cols}, 1fr)`,
          },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {kns.map((kn: any, i: number) => (
          <Box
            key={i}
            sx={{
              p: 1.5,
              ...stromaCompactPanel,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              variant="subtitle1"
              className="stroma-number"
              sx={{ fontFamily: 'var(--display)', fontWeight: 700, lineHeight: 1.2, fontSize: 22 }}
            >
              <ClaimValue claim={tileClaim(ladder, i)}>{formatKpiValue(kn.value, kn.format)}</ClaimValue>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.3 }}
            >
              {kn.label}
            </Typography>
            {kn.note && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', opacity: 0.8 }}>
                {kn.note}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  })());
  // in the card's buttons with the flag off; in the follow-up chips with it on (#1587)
  const pivots = answerPivots(turn, storedResult);
  const card = (
    // with the flag on (#1587) the answer is prose, as the mock's: no card frame around it
    <Paper elevation={0} sx={simple ? { bgcolor: 'transparent', border: 0, borderRadius: 0 } : { ...stromaAnswerCard, borderColor: 'divider' }} data-testid="answer-card">
      {(turn?.inherited || []).length > 0 && (
        <Box sx={simple ? { pb: 0.5 } : { px: 2, pt: 1.25 }}><InheritedChip inherited={turn?.inherited} /></Box>
      )}
      {selectionDetails}
      {usedDrafts.length > 0 && (
        <Box
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1,
            px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider',
            bgcolor: 'var(--warn-tint)', borderTopLeftRadius: 8, borderTopRightRadius: 8,
          }}
        >
          <WarningIcon sx={{ fontSize: 18, color: 'var(--warn)', mt: '1px' }} />
          <Typography variant="body2" sx={{ color: 'var(--warn)' }}>
            Built on {usedDrafts.length === 1 ? 'an unreviewed definition' : 'unreviewed definitions'}:{' '}
            <strong>
              {usedDrafts.map((d) => d.display_name || d.name).filter(Boolean).join(', ')}
            </strong>
            {/* No "drafted by" here. The banner now fires on the author's own
                drafts too, where naming them is odd, and the point is that
                nobody has CHECKED the wording — not who typed it. The author
                is on the Definitions page for anyone who needs it. */}
            . Nobody has reviewed the wording yet, so treat these numbers as
            provisional and don’t circulate them as the official figure.
          </Typography>
        </Box>
      )}

      {/* with the flag on (#1507) the tabs are the result card's panel, so the card is the summary alone */}
      {!simple && <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'var(--line-soft)', pr: 1, pl: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ flex: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 1, textTransform: 'none', px: 1.5, fontSize: 13.5 } }}
        >
          <Tab value="summary" label="Summary" />
          <Tab value="data" label="Data" disabled={workers.length === 0} />
          <Tab value="sql" label="SQL" disabled={workers.length === 0} />
          <Tab value="graph" label="Graph" disabled={!hasGraph} />
        </Tabs>
        <ExploreButton spec={exploreSpec(selectedWorker, ladder)} turnId={turn?.id} />
        {turn?.sessionId && <TraceLink sessionId={turn.sessionId} />}
      </Box>}

      <Box sx={simple ? { p: 0 } : { p: { xs: 2, sm: 2.5 } }}>
        <AnswerDeliveryStatus turn={turn} onSettledNote={simple ? setDeliveryNote : undefined} />
        {(simple || tab === 'summary') && (
          <>
            {labels.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: '72ch' }} data-testid="answer-label">
                {labels.join(' · ')}
              </Typography>
            )}
            {checkLines.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: '72ch' }} data-testid="answer-caveats">
                {checkLines.join(' ')}
              </Typography>
            )}
            {result?.mission_status === 'partial' && (
              <EvidenceChip tone="emerging" label="Partial results" size="small" sx={{ mb: 1.5 }} />
            )}
            {/* F1 (#1806): a Writer answer restored from a stored copy has no words until the server renders them for
                this reader; while that read is out it says so, and if it fails it says that — never a stale rendering */}
            {writerWordsStripped(turn) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}
                data-testid={answersLoading ? 'answer-loading' : 'answer-unloaded'}>
                {answersLoading ? 'Loading this answer…' : "This answer couldn't be loaded."}
              </Typography>
            )}
            {/* an answer of one number leads with it, large, its word beside it; the reply is the prose under it */}
            {figure && <Figure figure={figure} />}
            {shownHeadline && (
              <Typography
                variant="body1"
                data-testid={figure ? 'answer-prose' : undefined}
                sx={figure ? { mb: 2, lineHeight: 1.6, fontSize: 15, color: 'var(--ink-2)', maxWidth: '60ch' }
                  : { fontWeight: 600, mb: 2, lineHeight: 1.45, fontSize: 16, maxWidth: '72ch' }}
              >
                {shownHeadline}
              </Typography>
            )}
            {/* the rest of the loop's reply, and the line code writes when other readings agree (#2202) */}
            {synthesis?.loop && !!synthesis.narrative && <LoopWords text={synthesis.narrative} />}
            {synthesis?.loop && <LoopTables tables={((synthesis as any).tables || []).filter((t: any) => t.worker !== figure?.worker)} workers={workers} ladder={ladder} />}
            {/* what people said, beside the numbers of the same answer (#2203) */}
            {knowledgeBesideNumbers(result) && ladder?.specialist && (
              <Box sx={{ mb: 2, display: 'grid', gap: 1.5, maxWidth: '72ch' }} data-testid="loop-knowledge">
                <KnowledgeSections block={ladder.specialist} />
              </Box>
            )}
            {/* #1500 (flag on): routed as a why, answered without a walk (no change over time to explain) — said once */}
            {simple && ladder?.route?.routes?.includes('why') && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2, maxWidth: '72ch' }} data-testid="why-without-walk">
                This compares groups rather than a change over time, so here are the numbers behind it.
              </Typography>
            )}

            {/* #1489: exactly what was asked, first — each answer cell its frame cell, with that cell's claim */}
            {completedWorkers.filter((w) => w.sub_task_id !== figure?.worker && !((synthesis as any)?.tables || []).some((t: any) => t.worker === w.sub_task_id)).map((w) => (
              <AnswerTable key={w.sub_task_id} worker={w} claims={tableClaims(ladder, w.sub_task_id)} />
            ))}

            {highlights.length > 0 && (
              <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0 }}>
                {highlights.map((h, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <Typography variant="body2">{h}</Typography>
                  </li>
                ))}
              </Box>
            )}

            {stoppedWords && (stoppedWords.headline || stoppedWords.highlights.length > 0) && (
              <Box sx={{ mb: 2, maxWidth: '72ch' }} data-testid="stopped-words">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Stopped — this answer was not finished.</Typography>
                {stoppedWords.headline && (
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.45, fontSize: 16 }}>{stoppedWords.headline}</Typography>
                )}
                {stoppedWords.highlights.length > 0 && (
                  <Box component="ul" sx={{ pl: 3, mb: 0, mt: 0 }}>
                    {stoppedWords.highlights.map((h, i) => (
                      <li key={i} style={{ marginBottom: 6 }}>
                        <Typography variant="body2">{h}</Typography>
                      </li>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* with the flag on (#1500) the tiles are the panel's Context tab, as the mock's answer has none */}
            {!simple && keyNumberTiles}

            {/* the Graph tab's charts, inline under the numbers with the flag on (#1507), as the mock's chart */}
            {simple && hasGraph && (
              <Box sx={{ mb: 2.5 }} data-testid="inline-charts">
                {/* #1489: behind the supporting-numbers disclosure when the answer is shown, as the Graph tab */}
                {completedWorkers.some((w) => workerAnswer(w, tableClaims(ladder, w.sub_task_id)))
                  ? <SupportingCharts bundles={bundles} ladder={ladder} turnId={turn?.id ?? ''} />
                  : <Stack spacing={2}>{bundles.map((b: any, i: number) => <ChartBundle key={i} bundle={b} ladder={ladder} />)}</Stack>}
              </Box>
            )}

            {!normal && ladder?.policy && (ladder.policy.next_withheld || (typeof ladder.policy.next_hidden === 'number' && ladder.policy.next_hidden > 0)) && (
              <Box sx={{ mt: 3 }} data-testid="next-policy-note">
                <Typography variant="subtitle2" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Follow-ups</Typography>
                <HiddenNote policy={ladder.policy} count={ladder.policy.next_hidden} what="suggestions" />
              </Box>
            )}
            {!simple && !normal && synthesis?.follow_ups && synthesis.follow_ups.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                  Follow-ups
                </Typography>
                <Box>
                  {synthesis.follow_ups.map((f: string, i: number) => (
                    <Box
                      key={i}
                      onClick={() => sendLandingAgentQuery(f, followUpOf(turn))}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        py: 1.25,
                        px: 0.5,
                        borderTop: i === 0 ? 1 : 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        cursor: 'pointer',
                        color: 'text.secondary',
                        transition: 'color 120ms',
                        '&:hover': { color: 'primary.main' },
                        '&:hover .fu-text': { color: 'primary.main' },
                      }}
                    >
                      <SubdirectoryArrowRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      <Typography
                        className="fu-text"
                        variant="body2"
                        sx={{ color: 'text.primary', transition: 'color 120ms' }}
                      >
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* with the flag on (#1500) the answer's context is the panel's Context tab */}
            {!simple && ladder?.context && <AnswerContextLine context={ladder.context} perspective={ladder.perspective} />}

            {/* the anomaly bridge (#117 lite): any answered "what" turn can
                pivot into an RCA — the router inherits the metric from this
                turn's question via bare-follow-up matching */}
            {!simple && pivots && (
              <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {pivots.why && <Button
                  size="small" variant="outlined" startIcon={<QueryStatsIcon />}
                  onClick={pivots.why.ask}
                  sx={{ textTransform: 'none' }}
                >
                  {pivots.why.label}
                </Button>}
                <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}
                  disabled={pivots.next.disabled}
                  onClick={pivots.next.ask}
                >What's next</Button>
              </Box>
            )}

            {!simple && turn && turn.sessionId && (synthesis || narrative || headline || workers.length > 0) && (
              <FeedbackBar turn={turn} />
            )}
          </>
        )}

        {!simple && tab === 'data' && selectedWorker && (
          <DataTabBody workers={workers} selectedWorker={selectedWorker} onSelect={setSelectedSubTaskId} usedDrafts={usedDrafts} ladder={ladder} turnId={turn?.id ?? ''} />
        )}

        {!simple && tab === 'sql' && selectedWorker && (
          <SqlTabBody workers={workers} selectedWorker={selectedWorker} onSelect={setSelectedSubTaskId} />
        )}

        {!simple && tab === 'graph' && (completedWorkers.some((w) => workerAnswer(w, tableClaims(ladder, w.sub_task_id)))
          ? <SupportingCharts bundles={bundles} ladder={ladder} turnId={turn?.id ?? ''} />
          : (
            <Stack spacing={2}>
              {bundles.map((b: any, i: number) => (
                <ChartBundle key={i} bundle={b} ladder={ladder} />
              ))}
            </Stack>
          ))}
      </Box>
    </Paper>
  );
  // F4 (#1858 §4): while the answer runs, its shape is the frame under the words; the result card comes with the answer
  if (!simple || turn?.status === 'running') return card;
  // #1507 (flag on): one result card under the answer, opening the panel on Data, SQL and Trace
  const owner = turn?.id || 'current';
  const first = restingWorker(result) || workers[0];
  const rows = workers.reduce((n, w) => n + (typeof w.row_count === 'number' ? w.row_count : 0), 0);
  const panelTitle = first ? (first.description || subTaskLabel(first)) : (headline || 'This answer');
  const panelSubtitle = [
    workers.length ? `${rows} row${rows === 1 ? '' : 's'}` : null,
    workers.length ? `${workers.length} quer${workers.length === 1 ? 'y' : 'ies'}` : null,
    ...(result?.selectedPerspectives || []).map((p) => p.name),
  ].filter(Boolean).join(' · ');
  const exploring = exploreSpec(selectedWorker, ladder);
  const explore = exploring ? <ExploreButton spec={exploring} turnId={turn?.id} /> : null;
  const target = turn ? exploreOf(turn) : null;
  const panelTabs: PanelTabSpec[] = [
    ...(selectedWorker ? [
      { key: 'data' as PanelTab, toolbar: explore, body: () => <DataTabBody workers={workers} selectedWorker={selectedWorker} onSelect={setSelectedSubTaskId} usedDrafts={usedDrafts} ladder={ladder} turnId={turn?.id ?? ''} /> },
      { key: 'sql' as PanelTab, toolbar: explore, body: () => <SqlTabBody workers={workers} selectedWorker={selectedWorker} onSelect={setSelectedSubTaskId} /> },
    ] : []),
    ...traceTab(turn),
    ...(relatedLines(turn).length || ladder?.context || deliveryNote || keyNumberTiles ? [{ key: 'context' as PanelTab,
      body: () => <ContextTabBody turn={turn} context={ladder?.context} perspective={ladder?.perspective} delivery={deliveryNote} keyNumbers={keyNumberTiles} /> }] : []),
  ];
  return (
    <>
      {card}
      <InSlot at={slots?.row ?? null} slotted={!!slots}>
        <ResultCard owner={owner} tabs={panelTabs.map((t) => t.key)} primary={target && <ExploreAction target={target} turnId={owner} what={panelTitle} />} />
      </InSlot>
      <InSlot at={slots?.panel ?? null} slotted={!!slots}><ResultPanel owner={owner} subtitle={panelSubtitle} tabs={panelTabs} /></InSlot>
    </>
  );
}
